import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const bloodDrop = require('../../assets/imgs/blood_drop_black.png');

/**
 * RDW-style severity bar for the MVX four-band markers. Mirrors
 * RDWReferenceRangeChart exactly — a NORMALIZED axis so the bands are evenly
 * proportioned (the big optimal band 62.5%, three 12.5% severity bands), band
 * names centered ABOVE each band, boundary values labeled below, blood-drop
 * marker mapped piecewise into its zone.
 *
 * Default: green/Optimal on the LEFT (the index scores + GlycA, lower-better).
 * `reversed`: green/Optimal on the RIGHT (small HDL-P, higher-better) — the
 * three severity bands sit on the low/left end instead.
 */
const GREEN = '#78F880';
const YELLOW = '#FEFA83';
const ORANGE = '#FFD37E';
const RED = '#FC6552';
const GRID_LINE_COLOR = '#CDD7EA';

// Optimal band = 62.5%; the three severity bands 12.5% each.
const NORM_GREEN_LEFT = [0, 166.67, 200, 233.33, 266.67];
const NORM_GREEN_RIGHT = [0, 33.33, 66.67, 100, 266.67];

export type MvxSeverityBarProps = {
  /** Five value-space edges, low → high, matching the four segment order. */
  boundaries: number[];
  /** Band names in left→right order, e.g. ['Optimal','Lab','> Lab','Alarm']. */
  labels?: string[];
  value: number;
  format: (n: number) => string;
  /** Show the band-name labels above the bar. */
  showLabels?: boolean;
  /** Mirror the bar so green/Optimal is the big band on the high (right) end. */
  reversed?: boolean;
};

const reflowSoon = (chart: Highcharts.Chart): void => {
  setTimeout(() => {
    try {
      chart.reflow();
    } catch (e) {
      /* chart unmounted */
    }
  }, 0);
};

export class MvxSeverityBar extends React.Component<MvxSeverityBarProps> {
  private norm(): number[] {
    return this.props.reversed ? NORM_GREEN_RIGHT : NORM_GREEN_LEFT;
  }

  private colors(): string[] {
    return this.props.reversed ? [RED, ORANGE, YELLOW, GREEN] : [GREEN, YELLOW, ORANGE, RED];
  }

  // Map a value-space number to the normalized axis, piecewise-linear across the
  // four bands so the drop sits proportionally within its zone.
  private toNorm(value: number): number {
    const b = this.props.boundaries;
    const N = this.norm();
    if (value <= b[0]) return N[0];
    if (value >= b[4]) return N[4];
    for (let i = 0; i < 4; i++) {
      if (value <= b[i + 1]) {
        const t = (value - b[i]) / (b[i + 1] - b[i]);
        return N[i] + t * (N[i + 1] - N[i]);
      }
    }
    return N[4];
  }

  render() {
    const { boundaries, labels, value, format, showLabels, reversed } = this.props;
    const N = this.norm();
    const COLORS = this.colors();
    const normValue = this.toNorm(value);
    // Nudge the drop inward at the ends so it doesn't clip (as the app charts do).
    const offset = normValue >= 133.3 ? -12 : 12;

    // Map each normalized tick back to its value-space boundary for the number
    // label. Green-left shows the floor + three boundaries; green-right skips the
    // padded low end and shows the three meaningful boundaries.
    const numByPos: { [k: string]: number } = reversed
      ? { [String(N[1])]: boundaries[1], [String(N[2])]: boundaries[2], [String(N[3])]: boundaries[3] }
      : {
          [String(N[0])]: boundaries[0],
          [String(N[1])]: boundaries[1],
          [String(N[2])]: boundaries[2],
          [String(N[3])]: boundaries[3],
        };

    const options: Highcharts.Options = {
      chart: { type: 'bar', height: 60, marginTop: 20, marginBottom: 20, backgroundColor: 'transparent' },
      title: { text: '' },
      xAxis: [{ labels: { enabled: false }, tickLength: 0 }],
      yAxis: {
        title: { text: '' },
        min: 0,
        max: 266.67,
        tickPositions: N,
        labels: {
          enabled: true,
          formatter: function () {
            const key = String((this as any).value);
            return key in numByPos ? format(numByPos[key]) : '';
          },
          style: { fontSize: '10px', color: '#555' },
        },
        gridLineWidth: 1,
        gridLineColor: GRID_LINE_COLOR,
        plotBands: COLORS.map((color, i) => ({
          from: N[i],
          to: N[i + 1],
          color,
          label:
            showLabels && labels
              ? {
                  text: labels[i] ?? '',
                  align: 'center',
                  verticalAlign: 'top',
                  y: -5,
                  style: { color: '#333', fontSize: '10px', fontWeight: 'bold' },
                }
              : undefined,
        })),
      },
      tooltip: { enabled: false },
      series: [
        {
          type: 'bar',
          color: 'rgba(255,255,255,0)',
          borderWidth: 0,
          pointWidth: 10,
          data: [normValue],
          dataLabels: {
            enabled: true,
            allowOverlap: true,
            useHTML: true,
            verticalAlign: 'middle',
            x: offset,
            format: `<img src="${bloodDrop}" />`,
          },
        } as Highcharts.SeriesBarOptions,
      ],
      legend: { enabled: false },
      credits: { enabled: false },
      exporting: { enabled: false },
    };

    return (
      <HighchartsReact
        options={options}
        highcharts={Highcharts}
        callback={reflowSoon}
        containerProps={{ style: { width: '100%', overflow: 'hidden' } }}
      />
    );
  }
}

export default MvxSeverityBar;
